import i18next from 'i18next';
import { runBeforeLanguageChange, markSettingsSummaryPending } from '../utils/languageChangeHooks';
import { initReactI18next } from 'react-i18next';
import bundleDe from './bundle.de.json';
import {
  Lang, SUPPORTED_LANGS, DEFAULT_LANG, DEFAULT_ACTIVE_LANGS, LANG_LABELS, LANG_LOCALES,
  normalizeLang, sanitizeActiveLangs, fallbackChain, defaultLangForDevice as deviceDefault, localeFor,
} from './languages';

// ============================================
// Mehrsprachigkeit (Projekt 2026-09-17; weitere Sprachen 31.10.2026) – Laufzeit des Webs.
//
// - Nur das deutsche Bundle (Fallback) ist fest im Hauptbundle; die aktuelle Sprache kommt per
//   dynamischem import() als eigener Chunk, BEVOR die App gerendert wird (index.tsx wartet auf initI18n).
// - Danach wird das Backend-Bundle geholt, wenn dessen Version neuer ist als der Cache in
//   localStorage ('dc-i18n-<lang>'); gecacht wird nur die aktuelle Sprache (+ de).
// - Sprache: localStorage 'dc-lang' → Gerätesprache (unterstützt + aktiv → diese, sonst en).
// - Aktive Sprachen (Umschalter): /api/i18n/languages, Cache 'dc-langs', Start de,en.
// - Texte im Code: t('web.bereich.key'), Platzhalter {{name}}. Fallback-Kette Zielsprache → en → de.
// ============================================

export type { Lang } from './languages';
export { SUPPORTED_LANGS, DEFAULT_LANG, DEFAULT_ACTIVE_LANGS, LANG_LABELS, LANG_LOCALES, normalizeLang, sanitizeActiveLangs, fallbackChain, localeFor } from './languages';
export const LANG_STORAGE_KEY = 'dc-lang';
export const ACTIVE_LANGS_STORAGE_KEY = 'dc-langs';
const CACHE_PREFIX = 'dc-i18n-';
const REMOTE_WAIT_MS = 1500;
const API_BASE = (process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'https://dartcloud-backend.de').replace(/\/$/, '');

/** Suchmaschinen/Preview-Bots (pure): die Seite ist inhaltlich deutsch und wird deutsch indexiert. */
export const isCrawlerUserAgent = (ua: unknown): boolean =>
  typeof ua === 'string' && /bot|crawl|spider|slurp|googleother|google-inspectiontool|lighthouse|pagespeed|facebookexternalhit|facebot|twitterbot|linkedinbot|whatsapp|telegrambot|discordbot|slackbot|pinterest|embedly|prerender|headlesschrome/i.test(ua);

// ---------- aktive Sprachen ----------
export const readActiveLangs = (): Lang[] => {
  try {
    const raw = localStorage.getItem(ACTIVE_LANGS_STORAGE_KEY);
    return raw ? sanitizeActiveLangs(JSON.parse(raw)) : [...DEFAULT_ACTIVE_LANGS];
  } catch {
    return [...DEFAULT_ACTIVE_LANGS];
  }
};
let activeLangs: Lang[] = readActiveLangs();
const activeListeners = new Set<(langs: Lang[]) => void>();
/** Aktive Sprachen (Umschalter) – Start aus Cache/Default, dann Server. */
export const getActiveLangs = (): Lang[] => activeLangs;
export const onActiveLangsChange = (fn: (langs: Lang[]) => void): (() => void) => { activeListeners.add(fn); return () => { activeListeners.delete(fn); }; };
const setActiveLangs = (langs: Lang[]): void => {
  const next = sanitizeActiveLangs(langs);
  if (next.join(',') === activeLangs.join(',')) return;
  activeLangs = next;
  try { localStorage.setItem(ACTIVE_LANGS_STORAGE_KEY, JSON.stringify(next)); } catch { /* egal */ }
  activeListeners.forEach((fn) => fn(next));
};
/** /api/i18n/languages abfragen (nie werfend). */
export const refreshActiveLangs = async (): Promise<Lang[]> => {
  try {
    const res = await fetch(`${API_BASE}/api/i18n/languages`, { headers: { Accept: 'application/json' } });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data?.languages)) setActiveLangs(sanitizeActiveLangs(data.languages));
    }
  } catch { /* offline */ }
  return activeLangs;
};

/** Gerätesprache (pure): unterstützt + aktiv → diese, sonst en; de bleibt de. */
export const defaultLangForDevice = (deviceLocale: unknown, active: Lang[] = activeLangs): Lang => deviceDefault(deviceLocale, active);

/**
 * Startsprache (pure, Eingaben injizierbar): Crawler → de (SEO), sonst gespeicherte Wahl,
 * sonst Gerät (aktive Sprache → diese, sonst en).
 */
export const detectInitialLang = (stored: unknown, deviceLocale: unknown, userAgent: unknown = null, active: Lang[] = activeLangs): Lang =>
  isCrawlerUserAgent(userAgent) ? 'de' : (normalizeLang(stored) ?? defaultLangForDevice(deviceLocale, active));

/**
 * Erstbesuch (pure): keine gespeicherte Wahl, Gerätesprache unterstützt, aber laut Cache/Default (noch) nicht aktiv →
 * Kandidat, für den initI18n die Serverliste abwartet. Sonst sähe z. B. ein polnischer Erstbesucher Englisch, bis die
 * aktive Liste beim ZWEITEN Besuch im Cache liegt (Befund Live-Check 31.10.2026). Crawler nie, Deutsch nie (immer aktiv).
 */
export const deviceLangPendingActivation = (stored: unknown, deviceLocale: unknown, userAgent: unknown, active: Lang[]): Lang | null => {
  if (isCrawlerUserAgent(userAgent) || normalizeLang(stored)) return null;
  const device = normalizeLang(deviceLocale);
  return device && device !== DEFAULT_LANG && !active.includes(device) ? device : null;
};

export const readStoredLang = (): Lang | null => {
  try {
    return normalizeLang(localStorage.getItem(LANG_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const writeStoredLang = (lang: Lang): void => {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* privater Modus o. ä. */
  }
};

interface CachedBundle { version: number; translations: Record<string, string> }

const readCache = (lang: Lang): CachedBundle | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + lang);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.version !== 'number' || typeof parsed?.translations !== 'object') return null;
    return parsed as CachedBundle;
  } catch {
    return null;
  }
};

const writeCache = (lang: Lang, bundle: CachedBundle): void => {
  try {
    localStorage.setItem(CACHE_PREFIX + lang, JSON.stringify(bundle));
  } catch {
    /* Speicher voll / blockiert */
  }
};

/** Caches anderer Sprachen löschen (localStorage-Quote: ~500 KB je Sprache). Behält aktuelle Sprache + de. */
export const pruneBundleCaches = (keep: Lang[]): void => {
  try {
    const keepSet = new Set<string>(keep.map((l) => CACHE_PREFIX + l));
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX) && !keepSet.has(k)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch { /* egal */ }
};

/** Soll das Server-Bundle übernommen werden? (pure) */
export const shouldApplyRemote = (remoteVersion: unknown, cachedVersion: number | null): boolean =>
  typeof remoteVersion === 'number' && remoteVersion > 0 && (cachedVersion === null || remoteVersion > cachedVersion);

const applyBundle = (lang: Lang, translations: Record<string, string>): void => {
  i18next.addResourceBundle(lang, 'translation', translations, true, true);
};

/**
 * Eingebautes Bundle einer Sprache als eigener Chunk (pure Zuordnung, pure Funktion ohne Seiteneffekt).
 * Explizite import()-Aufrufe, damit der Bundler je Sprache einen Chunk baut; de ist statisch.
 */
export const loadBuiltinBundle = async (lang: Lang): Promise<Record<string, string>> => {
  switch (lang) {
    case 'de': return bundleDe as Record<string, string>;
    case 'en': return (await import(/* webpackChunkName: "i18n-en" */ './bundle.en.json')).default as Record<string, string>;
    case 'pl': return (await import(/* webpackChunkName: "i18n-pl" */ './bundle.pl.json')).default as Record<string, string>;
    case 'hu': return (await import(/* webpackChunkName: "i18n-hu" */ './bundle.hu.json')).default as Record<string, string>;
    case 'fr': return (await import(/* webpackChunkName: "i18n-fr" */ './bundle.fr.json')).default as Record<string, string>;
    case 'it': return (await import(/* webpackChunkName: "i18n-it" */ './bundle.it.json')).default as Record<string, string>;
    case 'nl': return (await import(/* webpackChunkName: "i18n-nl" */ './bundle.nl.json')).default as Record<string, string>;
    default: return bundleDe as Record<string, string>;
  }
};
const builtinLoaded = new Set<Lang>(['de']);
/** Eingebautes Bundle laden und als Basis anwenden (Cache/Server dürfen es überschreiben). Nie werfend. */
export const ensureBuiltinBundle = async (lang: Lang): Promise<void> => {
  if (builtinLoaded.has(lang)) return;
  try {
    const bundle = await loadBuiltinBundle(lang);
    // eingebaut = Basis; ein bereits angewendeter Cache (neuer) bleibt vorn (overwrite=false)
    i18next.addResourceBundle(lang, 'translation', bundle, true, false);
    builtinLoaded.add(lang);
  } catch {
    /* Chunk nicht ladbar (offline) → Fallback-Kette greift */
  }
};

// Synchron beim Modul-Import initialisieren: Module mit t()-Aufrufen auf oberster Ebene
// (Label-Konstanten) werden VOR initI18n() ausgewertet – deshalb muss der Katalog
// stehen, sobald dieses Modul geladen ist (index.tsx importiert es als erstes und wartet
// vor dem App-Import auf das Bundle der aktuellen Sprache).
const initialLang = detectInitialLang(
  readStoredLang(),
  typeof navigator !== 'undefined' ? navigator.language : null,
  typeof navigator !== 'undefined' ? navigator.userAgent : null,
);
i18next.use(initReactI18next).init({
  resources: { de: { translation: bundleDe } },
  lng: initialLang,
  fallbackLng: fallbackChain(initialLang).filter((l) => l !== initialLang),
  interpolation: { escapeValue: false, prefix: '{{', suffix: '}}' },
  returnNull: false,
  returnEmptyString: false,
  keySeparator: false,
  nsSeparator: false,
  initImmediate: false,
});
// Caches: nur aktuelle Sprache + de anwenden, alles andere räumen (Quote)
for (const l of Array.from(new Set<Lang>([initialLang, 'de']))) {
  const cached = readCache(l);
  if (cached) applyBundle(l, cached.translations);
}
pruneBundleCaches([initialLang, 'de']);
if (typeof document !== 'undefined') document.documentElement.lang = initialLang;

let startLang: Lang = initialLang;
/** Startsprache nach dem Modul-Import noch umstellen (Erstbesuch: Serverliste kennt die Gerätesprache). Läuft VOR dem App-Import. */
const switchStartLang = async (lang: Lang): Promise<void> => {
  i18next.options.fallbackLng = fallbackChain(lang).filter((l) => l !== lang);
  const cached = readCache(lang);
  if (cached) applyBundle(lang, cached.translations);
  await ensureBuiltinBundle(lang);
  await i18next.changeLanguage(lang);
  pruneBundleCaches([lang, 'de']);
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
  startLang = lang;
};

/** Backend-Bundle holen und anwenden, wenn neuer als der Cache. */
export const refreshFromServer = async (lang: Lang): Promise<boolean> => {
  const cached = readCache(lang);
  const res = await fetch(`${API_BASE}/api/i18n/bundle/${lang}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) return false;
  const data = await res.json();
  if (!shouldApplyRemote(data?.version, cached?.version ?? null)) return false;
  const translations = data?.translations && typeof data.translations === 'object' ? data.translations : {};
  writeCache(lang, { version: data.version, translations });
  applyBundle(lang, translations);
  return true;
};

/** Vor dem ersten Render (index.tsx): eingebautes Bundle der Sprache laden, kurz auf ein frischeres Server-Bundle warten. Nie werfend. */
export const initI18n = async (): Promise<Lang> => {
  // Erstbesuch mit einer Gerätesprache, die der Cache noch nicht als aktiv kennt: Serverliste abwarten (max. REMOTE_WAIT_MS)
  const pending = deviceLangPendingActivation(
    readStoredLang(),
    typeof navigator !== 'undefined' ? navigator.language : null,
    typeof navigator !== 'undefined' ? navigator.userAgent : null,
    activeLangs,
  );
  if (pending) {
    const fresh = await Promise.race([refreshActiveLangs(), new Promise<Lang[]>((resolve) => setTimeout(() => resolve(activeLangs), REMOTE_WAIT_MS))]);
    if (fresh.includes(pending)) {
      try { await switchStartLang(pending); } catch { /* bleibt bei der Startsprache */ }
    }
  }
  const lang = startLang;
  await ensureBuiltinBundle(lang);
  try {
    await Promise.race([
      Promise.all([refreshFromServer(lang).catch(() => false), refreshActiveLangs()]),
      new Promise((resolve) => setTimeout(resolve, REMOTE_WAIT_MS)),
    ]);
  } catch {
    /* offline o. ä. */
  }
  return lang;
};

/**
 * Sprache wechseln: Wahl speichern und Seite neu laden. Ein Reload ist bewusst –
 * Label-Konstanten auf Modulebene werden nur beim Laden übersetzt, und ein
 * Sprachwechsel ist selten. Vorher das Bundle der Zielsprache in den Cache holen.
 */
export const changeLanguage = async (lang: Lang): Promise<void> => {
  // Vor dem Wechsel: Haken (Länderfilter friert die wirksame Auswahl ein, damit sie nicht der neuen Sprache folgt)
  // und Merker für das Zusammenfassungs-Popup nach dem Neuladen (Marcel 01.11.2026)
  await runBeforeLanguageChange();
  markSettingsSummaryPending();
  writeStoredLang(lang);
  pruneBundleCaches([lang, 'de']);
  try {
    await Promise.race([refreshFromServer(lang).catch(() => false), new Promise((r) => setTimeout(r, REMOTE_WAIT_MS))]);
  } catch {
    /* egal – eingebautes Bundle reicht */
  }
  if (typeof window !== 'undefined') window.location.reload();
};

/**
 * Sprachwechsel in zwei Schritten (Marcel 01.11.2026: „wenn ich die Sprache wechsel, refresht die Seite sofort“ –
 * kein Popup sichtbar): [prepareLanguageChange] führt die Haken aus (Länder einfrieren), speichert die Wahl, lädt das
 * Bundle der neuen Sprache und liefert eine t-Funktion in der NEUEN Sprache für das Zusammenfassungs-Popup; erst
 * [reloadForLanguage] (nach OK) lädt die Seite neu. Es wird KEIN Sitzungs-Merker gesetzt – das Popup war schon da.
 */
export const prepareLanguageChange = async (lang: Lang): Promise<(key: string, params?: Record<string, unknown>) => string> => {
  await runBeforeLanguageChange();
  writeStoredLang(lang);
  pruneBundleCaches([lang, 'de']);
  await ensureBuiltinBundle(lang);
  try {
    await Promise.race([refreshFromServer(lang).catch(() => false), new Promise((r) => setTimeout(r, REMOTE_WAIT_MS))]);
  } catch { /* eingebautes Bundle reicht */ }
  i18next.options.fallbackLng = fallbackChain(lang).filter((l) => l !== lang);
  const fixed = i18next.getFixedT(lang);
  return (key, params) => fixed(key, params ? ({ replace: params } as any) : undefined) as string;
};

export const reloadForLanguage = (): void => { if (typeof window !== 'undefined') window.location.reload(); };

export const currentLang = (): Lang => normalizeLang(i18next.language) ?? 'de';

/** Locale der aktuellen Sprache für Intl ('de-DE', 'en-GB', 'pl-PL' …). */
export const appLocale = (): string => localeFor(currentLang());

/** Übersetzungsfunktion für den Code: t('web.home.title', { name }) */
export const t = (key: string, params?: Record<string, unknown>): string =>
  // Parameter NUR als Interpolationswerte übergeben – Namen wie lng, ns, count oder replace
  // dürfen nie als i18next-Optionen wirken (Absturz „lng.toLowerCase“, Turnierseite 2026-09-17)
  i18next.t(key, params ? ({ replace: params } as any) : undefined) as string;

export default i18next;
